<template>
	<li v-if="canCheckAvailability">
		<div
			class="margin-left-xl"
			role="button"
			@click="toggleModal"
		>
			<LinkInline
				class="no-padding"
				size="s"
				>{{ firstDeliveryWindowText || "Sjekk tidspunkt for levering hos deg" }}</LinkInline
			>
		</div>
		<Modal v-model="modal">
			<div class="modal">
				<div>
					<div class="flex">
						<TnIcon
							size="l"
							name="delivery"
							class="margin-right-s"
						/>
						<TnHeading
							type="h1"
							size="xs"
						>
							Levering
						</TnHeading>
					</div>
					<TnParagraph
						size="s"
						class="margin-top-s"
						>Søk på postnummer for å se når og hvordan vi kan levere.
					</TnParagraph>
				</div>
				<TnTextField
					v-model="postalCode"
					label="Postnummer"
					type="text"
					inputmode="numeric"
					maxlength="4"
					:loading="loadingPorterBuddyAvailability"
					:disabled="loadingPorterBuddyAvailability"
					@update:modelValue="checkPorterBuddyDeliveryAvailability(postalCode)"
				>
					<template
						v-if="!postalCodeValid"
						#error
					>
						{{ postalCodeErrorMessage }}
					</template>
				</TnTextField>
				<TnParagraph
					class="italic"
					size="s"
					>Leveringstidspunkt velger du når du bestiller.</TnParagraph
				>
				<ul class="delivery-options-list">
					<ECommerceDeliveryMethodCard
						v-for="option in availableShippingOptions"
						:key="option.name"
						:deliveryMethod="option"
					/>
				</ul>
			</div>
		</Modal>
	</li>
</template>

<script>
import customers from "~/integrations/customers";
import telenorid from "~/telenorid/telenorid";
import { mapMutations, mapActions, mapGetters, mapState } from "vuex";
import ecommerceIntegration from "~/integrations/ecommerce-v2-integration";
import dateHelper from "~/helpers/dateHelper";

export default defineNuxtComponent({
	name: "ECommerceDeliveryAvailability",

	data: () => ({
		postalCode: "",
		availability: null,
		modal: false,
		deliveryOptions: [],
		postalCodeErrorMessage: "Du er nødt til å oppgi et gyldig postnummer på 4 siffer.",
	}),

	methods: {
		...mapMutations("ecommerce", ["setDeliveryAvailability", "setDeliveryPostalCode"]),
		...mapActions("ecommerce", ["checkPorterBuddyDeliveryAvailability"]),
		toggleModal() {
			this.modal = !this.modal;

			if (this.modal && this.postalCode && !this.isDeliveryWindowOpen) {
				this.checkPorterBuddyDeliveryAvailability(this.postalCode);
			}
		},
		async getDeliveryOptions(context) {
			const res = await ecommerceIntegration.getDeliveryOptions(context);
			return { ...res, shippingOptions: res.shippingOptions.filter((option) => option.enabled) };
		},
		findDeliveryOptionByName(name) {
			return this.deliveryOptions?.shippingOptions?.find((option) => option.name.toLowerCase() === name.toLowerCase());
		},
	},

	computed: {
		...mapGetters("ecommerce", ["isDeliveryWindowOpen", "firstDeliveryWindow"]),
		...mapState("ecommerce", [
			"product",
			"deliveryAvailability",
			"deliveryPostalCode",
			"loadingPorterBuddyAvailability",
		]),
		availableShippingOptions() {
			return this.deliveryOptions?.shippingOptions?.filter(
				(option) =>
					option.name.toLowerCase() != "porterbuddy" || (this.isDeliveryWindowOpen && this.firstDeliveryWindow),
			);
		},
		canCheckAvailability() {
			return (
				!this.product?.disablePorterbuddy &&
				this.deliveryOptions?.shippingOptions?.some((option) => option.name.toLowerCase() === "porterbuddy")
			);
		},
		postalCodeValid() {
			return this.postalCode?.length === 0 || /^[0-9]{4}$/.test(this.postalCode);
		},
		firstDeliveryWindowText() {
			if (this.isDeliveryWindowOpen && this.firstDeliveryWindow) {
				return `Vi kan levere til postnummer ${
					this.deliveryAvailability.destinationResolvedAddress?.postalCode
				} ${this.day} mellom ${dateHelper.getTime(this.deliveryWindowStart)} og ${dateHelper.getTime(this.deliveryWindowEnd)}.`;
			}

			return null;
		},
		day() {
			if (dateHelper.isToday(this.deliveryWindowStart)) {
				return "i dag";
			} else if (dateHelper.isTomorrow(this.deliveryWindowStart)) {
				return "i morgen";
			} else {
				return this.deliveryWindowStart.toLocaleDateString("nb-no", { weekday: "long", month: "long", day: "numeric" });
			}
		},
		deliveryWindowStart() {
			return new Date(this.firstDeliveryWindow.start);
		},
		deliveryWindowEnd() {
			return new Date(this.firstDeliveryWindow.end);
		},
	},

	async mounted() {
		this.deliveryOptions = await this.getDeliveryOptions("privat");

		if (this.canCheckAvailability) {
			const user = await telenorid.getOrLoginUser();

			if (user) {
				const customer = await customers.getCustomerDataWithoutCache();

				if (this.deliveryPostalCode || customer?.postalAddress?.postalCode || customer?.address?.postalCode) {
					if (!this.postalCode)
						this.postalCode =
							this.deliveryPostalCode || customer?.postalAddress?.postalCode || customer?.address?.postalCode;

					if (!this.isDeliveryWindowOpen || this.postalCode !== this.deliveryPostalCode) {
						this.checkPorterBuddyDeliveryAvailability(this.postalCode);
					}
				}
			}
		}
	},
});
</script>

<style lang="scss" scoped>
.modal {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.flex {
	display: flex;
	justify-content: flex-start;
	align-items: center;
}

.italic {
	font-style: italic;
}

.no-padding {
	padding: 0;
}

.delivery-options-list {
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 16px;
}
</style>
